/**
 * Admin / Data Health (PRD 10.2, 15).
 *
 * Answers the question a data analyst actually has: which provider supplied
 * what, how fresh is it, and how much of the inventory does it cover. Weights
 * and scoring versions are shown read-only here; publishing one is an admin
 * action against the API.
 */

import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { CadenceStatus, DataHealth as DataHealthPayload } from '../lib/types'
import { percent, shortDate, titleCase } from '../lib/format'
import { Callout, ErrorCallout } from '../components/Callout'
import { Chip, DataClassChip, FreshnessChip } from '../components/Chips'
import { Metric, Skeleton } from '../components/Metric'

/**
 * Refresh-cadence fields the API adds to every provider row. Declared here so
 * this page can surface them without waiting on the shared API type; the cast
 * below is read-only and confined to this table.
 */
type ProviderCadence = DataHealthPayload['providers'][number]

const CADENCE_LABEL: Record<CadenceStatus, string> = {
  within_cadence: 'Within cadence',
  overdue: 'Overdue',
  no_data: 'No data loaded',
  observation_date_missing: 'Age unknown',
  cadence_undeclared: 'No cadence declared',
}

function CadenceCell({ cadence }: { cadence: ProviderCadence }) {
  // Only a real comparison earns a pass/fail tone: an age that could not be
  // established stays neutral, so "unknown" never reads as "fresh".
  const tone =
    cadence.cadence_status === 'within_cadence'
      ? 'ok'
      : cadence.cadence_status === 'overdue'
        ? 'danger'
        : 'neutral'
  return (
    <>
      <Chip tone={tone} title={cadence.cadence_note}>
        {CADENCE_LABEL[cadence.cadence_status]}
        {cadence.days_overdue ? ` +${cadence.days_overdue}d` : ''}
      </Chip>
      <div className="tiny faint">
        {cadence.latest_observation_date
          ? `${shortDate(cadence.latest_observation_date)} · ${cadence.data_age_days}d old`
          : 'No observation date'}
      </div>
    </>
  )
}

export function DataHealth() {
  const health = useQuery<DataHealthPayload>({
    queryKey: ['data-health'],
    queryFn: api.dataHealth,
  })
  const configs = useQuery({ queryKey: ['scoring-configs'], queryFn: api.scoringConfigs })
  const audit = useQuery({ queryKey: ['audit'], queryFn: () => api.auditLog() })

  if (health.isLoading) {
    return (
      <main className="main">
        <div className="page">
          <Skeleton rows={6} />
        </div>
      </main>
    )
  }
  if (health.isError) {
    return (
      <main className="main">
        <div className="page">
          <ErrorCallout error={health.error} onRetry={() => void health.refetch()} />
        </div>
      </main>
    )
  }

  const data = health.data!
  const snapshots = data.feature_snapshots

  return (
    <main className="main">
      <div className="page">
        <div className="page-head">
          <div className="grow">
            <h1>Data health</h1>
            <div className="page-sub">
              Provider coverage, freshness and the scoring versions in force for tenant{' '}
              <span className="mono">{data.tenant_id}</span>.
            </div>
          </div>
          {data.tenant_is_synthetic ? <Chip tone="synthetic">Sample data tenant</Chip> : null}
        </div>

        <Callout tone="info" title="How missing data is treated">
          {data.note}
        </Callout>

        <div className="grid four">
          {Object.entries(data.row_counts).map(([table, count]) => (
            <Metric
              key={table}
              label={titleCase(table.replace(/^geo_/, ''))}
              value={count.toLocaleString('en-IN')}
            />
          ))}
        </div>

        <div className="card">
          <div className="card-head">
            <h2 className="grow">Providers</h2>
            <span className="tiny faint">Lower precedence wins a disagreement</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Provider</th>
                  <th>Domain</th>
                  <th>Method</th>
                  <th className="num">Refresh</th>
                  <th>Data age</th>
                  <th className="num">Precedence</th>
                  <th>Coverage note</th>
                </tr>
              </thead>
              <tbody>
                {data.providers.map((provider) => (
                  <tr key={provider.id}>
                    <td>
                      <div style={{ fontWeight: 550 }}>{provider.name}</div>
                      <div className="tiny mono faint">{provider.id}</div>
                    </td>
                    <td>
                      <Chip>{provider.domain}</Chip>
                    </td>
                    <td className="mono tiny">{provider.methodology_version}</td>
                    <td className="num tnum">
                      {provider.refresh_cadence_days ? `${provider.refresh_cadence_days}d` : '—'}
                    </td>
                    <td className="small">
                      <CadenceCell cadence={provider} />
                    </td>
                    <td className="num tnum">{provider.precedence}</td>
                    <td className="small muted">
                      {provider.is_synthetic ? (
                        <Chip tone="synthetic">synthetic</Chip>
                      ) : (
                        <Chip tone="ok">contracted</Chip>
                      )}{' '}
                      {provider.coverage_note}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid two">
          <div className="card">
            <div className="card-head">
              <h2 className="grow">Feature snapshot freshness</h2>
            </div>
            <div className="card-body stack tight">
              {Object.entries(snapshots.freshness).map(([state, count]) => (
                <div key={state} className="row between">
                  <FreshnessChip value={state as any} />
                  <span className="tnum">{count.toLocaleString('en-IN')}</span>
                </div>
              ))}
              <div className="tiny faint">
                Fresh under {data.thresholds.aging_after_days} days, stale beyond{' '}
                {data.thresholds.stale_after_days} days. Cache TTL{' '}
                {data.thresholds.feature_cache_ttl_seconds}s.
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head">
              <h2 className="grow">How values were obtained</h2>
            </div>
            <div className="card-body stack tight">
              {Object.entries(snapshots.data_classes).map(([cls, count]) => (
                <div key={cls} className="row between">
                  <DataClassChip value={cls as any} />
                  <span className="tnum">{count.toLocaleString('en-IN')}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {Object.keys(data.poi_by_category).length ? (
          <div className="card">
            <div className="card-head">
              <h2 className="grow">POI coverage by category</h2>
            </div>
            <div className="card-body pill-row">
              {Object.entries(data.poi_by_category)
                .sort((a, b) => b[1] - a[1])
                .map(([category, count]) => (
                  <Chip key={category}>
                    {titleCase(category)} {count}
                  </Chip>
                ))}
            </div>
          </div>
        ) : null}

        {Object.keys(snapshots.assets_covered_per_feature).length ? (
          <details className="card">
            <summary className="card-head" style={{ cursor: 'pointer' }}>
              <h2 className="grow">Assets covered per feature</h2>
            </summary>
            <div className="table-wrap" style={{ maxHeight: 380 }}>
              <table>
                <thead>
                  <tr>
                    <th>Feature key</th>
                    <th className="num">Assets with data</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(snapshots.assets_covered_per_feature)
                    .sort((a, b) => a[1] - b[1])
                    .map(([key, count]) => (
                      <tr key={key}>
                        <td className="mono tiny">{key}</td>
                        <td className="num tnum">{count}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </details>
        ) : null}

        {configs.data?.length ? (
          <div className="card">
            <div className="card-head">
              <h2 className="grow">Scoring versions</h2>
              <span className="tiny faint">Published versions are immutable</span>
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Objective</th>
                    <th>Weights</th>
                    <th>Missing-feature policy</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.data.map((config: any, i: number) => (
                    <tr key={i}>
                      <td className="mono">{config.scoring_version}</td>
                      <td className="small">{titleCase(config.objective)}</td>
                      <td className="pill-row">
                        {Object.entries(config.weights as Record<string, number>)
                          .filter(([, weight]) => weight > 0)
                          .sort((a, b) => b[1] - a[1])
                          .map(([component, weight]) => (
                            <Chip key={component}>
                              {titleCase(component)} {percent(weight)}
                            </Chip>
                          ))}
                      </td>
                      <td className="small muted">
                        {config.missing_feature_policy.replace(/_/g, ' ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="card-body tight tiny faint">
              {configs.data[0]?.notes as string}
            </div>
          </div>
        ) : null}

        {audit.data?.length ? (
          <details className="card">
            <summary className="card-head" style={{ cursor: 'pointer' }}>
              <h2 className="grow">Recent audit events</h2>
            </summary>
            <div className="table-wrap" style={{ maxHeight: 380 }}>
              <table>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>Object</th>
                    <th>Actor</th>
                    <th>Scoring</th>
                  </tr>
                </thead>
                <tbody>
                  {audit.data.map((event: any, i: number) => (
                    <tr key={i}>
                      <td className="tiny nowrap">{shortDate(event.occurred_at)}</td>
                      <td className="small mono">{event.action}</td>
                      <td className="tiny muted">
                        {event.object_type}
                        <div className="mono faint">{String(event.object_id ?? '').slice(0, 20)}</div>
                      </td>
                      <td className="small">{event.actor_email ?? '—'}</td>
                      <td className="tiny mono">{event.scoring_version ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        ) : null}
      </div>
    </main>
  )
}
